
import React, { useMemo, useState } from "react";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/axios";

const INITIAL = {
  issue_type: "",
  description: "",
  priority: "Medium",
  urgency_level: "Medium",
  location: "",
  asset_number: "",
};

const PRIORITY_OPTIONS = ["High", "Medium", "Low"];
const URGENCY_OPTIONS = ["High", "Medium", "Low"];

const AddTaskModal = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setFormData(INITIAL);
    setSubmitting(false);
    setError("");
    setSuccess(false);
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && !submitting) {
      resetForm();
    }
    setOpen(nextOpen);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (error) setError("");
    if (success) setSuccess(false);
  };

  const canSubmit = useMemo(() => {
    const { issue_type, description, priority, urgency_level } = formData;
    if (!issue_type.trim() || !description.trim()) return false;

    const allowed = new Set(["Low", "Medium", "High"]);
    return allowed.has(priority) && allowed.has(urgency_level);
  }, [formData]);

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    setOpen(false);
  };

  async function handleSubmit(e) {
    if (e?.preventDefault) e.preventDefault();
    if (submitting || !canSubmit) return;

    setError("");
    setSuccess(false);
    setSubmitting(true);

    try {
      await api.post("/solo-mdsh-api/maintenance/tasks", {
        issue_type: formData.issue_type.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        urgency_level: formData.urgency_level,
        location: formData.location?.trim() || null,
        asset_number: formData.asset_number?.trim() || null,
      });

      setSuccess(true);
      onSuccess?.();

      setTimeout(() => {
        resetForm();
        setOpen(false);
      }, 500);
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Internal server error. Please try again.";

      if (status === 404) {
        setError("Endpoint not found (404). Check API mount: /solo-mdsh-api/maintenance.");
      } else if (status === 405) {
        setError("Method not allowed (405). Verify POST is enabled on the API route.");
      } else if (status === 401 || status === 403) {
        setError("Not authorized. Please log in again and confirm your role or unit access.");
      } else if (status === 422) {
        setError(`Validation error: ${msg}`);
      } else {
        setError(`Failed to submit task: ${msg}`);
      }

      console.error("Failed to submit task:", err?.response || err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-700 text-white hover:bg-emerald-600 shadow-sm">
          ➕ Add Task
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-xl rounded-2xl border border-slate-700 bg-[#0f172a] text-white shadow-2xl p-0 overflow-hidden">
        <div className="border-b border-slate-800 bg-gradient-to-r from-emerald-900/40 via-slate-900 to-cyan-900/30 px-6 py-5">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold tracking-tight text-white">
              🛠 Report New Maintenance Task
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-300">
              Create a new maintenance request with issue details, urgency, and optional asset/location reference.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-6">
          {error ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {success ? (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
              ✅ Request submitted successfully.
            </div>
          ) : null}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-200">Issue Type *</label>
            <input
              type="text"
              placeholder="Example: Oven failure, freezer alarm, leaking tap"
              value={formData.issue_type}
              onChange={handleChange("issue_type")}
              className="h-11 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 text-sm text-white placeholder:text-slate-500 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-200">Description *</label>
            <textarea
              placeholder="Describe the issue clearly, including symptoms, impact, and anything already checked..."
              value={formData.description}
              onChange={handleChange("description")}
              className="min-h-[120px] w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-200">Priority *</label>
              <select
                value={formData.priority}
                onChange={handleChange("priority")}
                className="h-11 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 text-sm text-white outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
              >
                {PRIORITY_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value === "High" ? "🔥 High" : value === "Medium" ? "⚠️ Medium" : "💤 Low"}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-200">Urgency *</label>
              <select
                value={formData.urgency_level}
                onChange={handleChange("urgency_level")}
                className="h-11 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 text-sm text-white outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
              >
                {URGENCY_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value === "High" ? "🚨 High" : value === "Medium" ? "⏳ Medium" : "🕓 Low"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-200">Location</label>
              <input
                type="text"
                placeholder="Kitchen, bar, cold room, loading bay..."
                value={formData.location}
                onChange={handleChange("location")}
                className="h-11 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 text-sm text-white placeholder:text-slate-500 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-200">Asset Number</label>
              <input
                type="text"
                placeholder="Asset reference if known"
                value={formData.asset_number}
                onChange={handleChange("asset_number")}
                className="h-11 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 text-sm text-white placeholder:text-slate-500 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
              />
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3 text-xs text-slate-400">
            Fields marked with * are required.
          </div>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
              className="border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={submitting || !canSubmit}
              className="bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-60"
            >
              {submitting ? "Submitting..." : "🚀 Submit Task"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTaskModal;